import type { OrderKind, OrderSide } from './constants';

// All user-facing Simulate Trade copy.
export const SIMULATE_COPY = {
  trigger: 'Simulate Trade',
  dialog: {
    eyebrow: 'Trade simulator',
    title: 'Simulate a master trade',
    intro: 'Preview the order your slave account would place. Nothing is sent to your broker.',
    close: 'Close simulator'
  },
  actions: {
    reset: 'Reset',
    simulate: 'Simulate',
    edit: 'Edit order',
    again: 'Simulate again',
    retry: 'Try again'
  },
  result: {
    title: 'Simulation result',
    copied: 'This trade will be copied',
    skipped: "This trade won't be copied",
    notes: (count: number) => (count === 0 ? 'No notes from the copier' : `${count} note${count === 1 ? '' : 's'} from the copier`),
    noOrder: 'The copier skipped this trade without returning a reason.',
    pendingHeld: {
      title: 'No new trade on the slave',
      caption: 'The slave copies pending orders, so it already holds its own copy of this order. The copier opens no second trade.'
    },
    master: 'Master · sent',
    slave: 'Slave · would open',
    rows: {
      symbol: 'Symbol',
      order: 'Order',
      lots: 'Lots',
      openPrice: 'Open price',
      stopLoss: 'Stop loss',
      takeProfit: 'Take profit',
      magic: 'Magic',
      comment: 'Comment',
      placedType: 'Placed type',
      expiration: 'Expiration',
      delay: 'Delayed until'
    },
    changes: {
      mapped: 'Mapped',
      reversed: 'Reversed',
      changed: 'Changed',
      notCopied: 'Not copied',
      added: 'Added',
      ratio: (ratio: string) => `×${ratio}`
    },
    fromPending: 'From pending order',
    brokerTpSl: 'Broker-side SL / TP',
    tpSl: {
      fixedStopLoss: 'Fixed SL',
      fixedTakeProfit: 'Fixed TP',
      relativeStopLoss: 'Relative SL',
      relativeTakeProfit: 'Relative TP'
    },
    why: 'Why'
  },
  mapping: {
    saveTitle: 'Save this mapping for the slave?',
    updateTitle: "Update the slave's mapping?",
    explain: (master: string, slave: string) => `Future ${master} trades will open as ${slave} on this slave.`,
    dismiss: 'Not now',
    save: 'Save mapping',
    update: 'Update mapping',
    saved: 'Mapping saved',
    failed: 'Failed to save mapping'
  },
  failure: {
    title: 'Simulation failed',
    fallback: 'Something went wrong. Try again in a moment.'
  },
  // The first unmet requirement is shown beside the Simulate button.
  invalid: {
    accounts: 'Select master and slave accounts first',
    symbol: 'Pick a symbol',
    lots: 'Enter lots above 0'
  },
  form: {
    side: 'Side',
    kind: 'Order type',
    sides: { Buy: 'Buy', Sell: 'Sell' } as Record<OrderSide, string>,
    kinds: { Market: 'Market', Limit: 'Limit', Stop: 'Stop' } as Record<OrderKind, string>,
    symbol: 'Symbol',
    lots: 'Lots',
    lotsUnit: 'lots',
    placeholders: { lots: '0.01', number: '0' },
    openPrice: 'Open price',
    stopLoss: 'Stop loss',
    takeProfit: 'Take profit',
    priceHint: 'Add the open price so the slave can measure the SL/TP distance.',
    pendingFill: {
      title: 'Came from a filled pending order',
      hint: 'If the slave copies pending orders, it already holds this order, so no new trade opens.'
    },
    more: 'More options',
    moreSet: (count: number) => `${count} set`,
    magic: 'Magic number',
    comment: 'Comment',
    placedType: 'Placed type',
    expiration: 'Expiration',
    expirationPast: 'Expiration must be in the future',
    slaveSymbol: 'Slave symbol override',
    slaveSymbolHint: 'Leave empty to use the matching symbol on the slave account.'
  },
  route: {
    master: 'Master',
    slave: 'Slave',
    copiesTo: 'copies to'
  },
  /** Footer read-back, e.g. "Buy Limit 0.5 EURUSD @ 1.0845". */
  summary: (side: string, kind: string, lots: number, symbol: string, price: string) =>
    [side, kind, lots, symbol, '@', price].filter((part) => part !== '').join(' '),
  marketPrice: 'market',
  empty: '—',
  block: {
    accounts: 'Select master account, slave account, risk type and risk value first',
    accountsShort: 'Select master and slave accounts first',
    riskType: 'Select a risk type first',
    riskValue: 'Enter a valid risk value first',
    riskSettings: 'Complete risk settings first'
  }
};
